import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Modal,
  Paper,
  Slide,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { BiLogoGmail } from "react-icons/bi";
import { PiMicrosoftOutlookLogoFill } from "react-icons/pi";
import { SiZoho } from "react-icons/si";
import { EditRounded, EmailRounded, KeyboardArrowDown } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useMsal } from "@azure/msal-react";
import { useGoogleLogin } from "@react-oauth/google";
import axios from "axios";
import Key from "../../../clientVariables.json";
import { loginRequest } from "../../../helpers/msalConfig";
import { myCandidatesSelector } from "../redux/selector";
import {
  selectedSubmissionAction,
  sendCandidateProfileToVendors,
  selectedResume,
} from "../redux/action";
import Step3 from "./Submissions/Step3";
import SendCandidateToVendorEmail from "./Submissions/emailTemplates/SendCandidateToVendorEmail";

const SlideTransition = React.forwardRef(function SlideTransition(props, ref) {
  return <Slide direction="left" ref={ref} {...props} timeout={{ enter: 500, exit: 400 }} />;
});

const buildBulkSubmission = (candidateDetails, firstJob) => ({
  id: `bulk-${firstJob?.id}`,
  candidate_id: candidateDetails?.candidate_id,
  job_id: firstJob?.id,
  jobtitle: firstJob?.jobtitle || "Multiple Positions",
  email_id: firstJob?.email_id || "",
  contact_name: firstJob?.contact_name || firstJob?.company_name || "",
  filename: "",
});

export default function JobsSendToVendorModal({
  open,
  onClose,
  selectedJobIds = [],
  selectedJobs = [],
}) {
  const dispatch = useDispatch();
  const { instance, accounts } = useMsal();
  const {
    candidateDetails,
    selectedSubmission,
    submissionSelectedResume,
  } = useSelector(myCandidatesSelector);
  const currentUser = useSelector((state) => state.userManagement.user);

  const [sendProfileToVendorEmailPreview, setSendProfileToVendorEmailPreview] = useState(false);
  const [step3EmailData, setStep3EmailData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [primaryResumeId, setPrimaryResumeId] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [gmailAccessToken, setGmailAccessToken] = useState(null);
  const [outlookToken, setOutlookToken] = useState(null);
  const [zohoEmail, setZohoEmail] = useState(() => sessionStorage.getItem("zoho_email") || null);
  const [zohoAccessToken, setZohoAccessTokenState] = useState(() => sessionStorage.getItem("zoho_token"));
  const [zohoAccountsServer, setZohoAccountsServerState] = useState(() =>
    sessionStorage.getItem("zoho_accounts_server")
  );

  const previousSubmissionRef = useRef(null);
  const skipRestoreOnCloseRef = useRef(false);
  const menuOpen = Boolean(anchorEl);
  const sharedData = {};

  const setZohoAccessToken = (token) => {
    if (token) sessionStorage.setItem("zoho_token", token);
    else sessionStorage.removeItem("zoho_token");
    setZohoAccessTokenState(token);
  };

  const setZohoAccountsServer = (server) => {
    if (server) sessionStorage.setItem("zoho_accounts_server", server);
    else sessionStorage.removeItem("zoho_accounts_server");
    setZohoAccountsServerState(server);
  };

  const firstJob = selectedJobs[0] || { id: selectedJobIds[0] };

  useEffect(() => {
    if (!open) return;

    previousSubmissionRef.current = selectedSubmission;
    const bulkSubmission = buildBulkSubmission(candidateDetails, firstJob);
    dispatch(selectedSubmissionAction(bulkSubmission));

    const fetchPrimaryResume = async () => {
      try {
        if (!candidateDetails?.candidate_id) return;
        const response = await axios.post(`${Key.domain}/candidate/get_primary_resume_details`, {
          candidate_id: candidateDetails.candidate_id,
        });
        const resume = response.data.data[0];
        const resumeId = resume?.id || resume?.resume_id;
        setPrimaryResumeId(resumeId);
        if (resume) dispatch(selectedResume(resume));
      } catch (error) {
        console.error("Error fetching primary resume:", error);
      }
    };

    fetchPrimaryResume();

    return () => {
      if (!skipRestoreOnCloseRef.current && previousSubmissionRef.current) {
        dispatch(selectedSubmissionAction(previousSubmissionRef.current));
      }
      skipRestoreOnCloseRef.current = false;
    };
  }, [open, candidateDetails?.candidate_id, firstJob?.id]);

  useEffect(() => {
    const handleZohoSuccess = (e) => {
      const { email, token, accountsServer } = e.detail || {};
      if (email) {
        setZohoEmail(email);
        sessionStorage.setItem("zoho_email", email);
      }
      if (token) setZohoAccessToken(token);
      if (accountsServer) setZohoAccountsServer(accountsServer);
    };

    window.addEventListener("zoho_auth_success", handleZohoSuccess);

    const token = localStorage.getItem("zoho_token");
    const server = localStorage.getItem("zoho_accounts_server");
    const email = localStorage.getItem("zoho_email");

    if (token) {
      setZohoAccessToken(token);
      setZohoAccountsServer(server);
      if (email) {
        setZohoEmail(email);
        sessionStorage.setItem("zoho_email", email);
        localStorage.removeItem("zoho_email");
      }
      localStorage.removeItem("zoho_token");
      localStorage.removeItem("zoho_accounts_server");
    }

    return () => window.removeEventListener("zoho_auth_success", handleZohoSuccess);
  }, []);

  useEffect(() => {
    const initializeOutlookAuth = () => {
      const activeAccount = instance.getActiveAccount();
      if (!activeAccount && accounts.length > 0) {
        instance.setActiveAccount(accounts[0]);
      }
    };
    initializeOutlookAuth();
  }, [instance, accounts]);

  const step3EmailDataHandler = (data) => {
    setStep3EmailData(data);
  };

  const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const acquireOutlookToken = async () => {
    let account = instance.getActiveAccount();
    if (!account && accounts.length > 0) {
      account = accounts[0];
      instance.setActiveAccount(account);
    }

    if (!account) {
      const loginResponse = await instance.loginPopup({
        ...loginRequest,
        prompt: "select_account",
      });
      account = loginResponse.account;
      instance.setActiveAccount(account);
    }

    const tokenResponse = await instance.acquireTokenSilent({
      ...loginRequest,
      account,
      loginHint: account.username,
    });

    setOutlookToken(tokenResponse.accessToken);
    return tokenResponse.accessToken;
  };

  const gmailLogin = useGoogleLogin({
    scope: "https://www.googleapis.com/auth/gmail.send",
    onSuccess: (tokenResponse) => {
      setGmailAccessToken(tokenResponse.access_token);
    },
    onError: () => {
      console.error("Gmail authentication failed");
    },
  });

  const handleZohoLogin = () => {
    const CLIENT_ID = "1000.4OII743C9WQ1SSD0B2WZXEUE38X1YK";
    const REDIRECT_URI = "http://bs.jobsnprofiles.com/zoho/callback";

    const stateData = {
      candidateId: candidateDetails?.candidate_id,
      jobId: selectedJobIds[0],
      source: "jobs_bulk",
      jobIds: selectedJobIds,
    };

    sessionStorage.setItem("jobs_bulk_selected_ids", JSON.stringify(selectedJobIds));
    sessionStorage.setItem("zoho_redirect_jobs", "1");

    if (step3EmailData) {
      sessionStorage.setItem("saved_step3_data", JSON.stringify(step3EmailData));
    }

    const encodedState = btoa(JSON.stringify(stateData));
    const authUrl =
      `https://accounts.zoho.com/oauth/v2/auth` +
      `?scope=ZohoMail.messages.CREATE,ZohoMail.accounts.READ,profile,AaaServer.profile.Read` +
      `&client_id=${CLIENT_ID}` +
      `&response_type=code` +
      `&redirect_uri=${encodeURIComponent(REDIRECT_URI)}` +
      `&state=${encodedState}` +
      `&access_type=offline` +
      `&prompt=login`;

    window.location.href = authUrl;
  };

  const handleSendEmail = async (provider, token = null, accountsServer = null) => {
    const selectedResumeId =
      submissionSelectedResume?.resume_id && submissionSelectedResume?.resume_id !== 0
        ? submissionSelectedResume.resume_id
        : step3EmailData?.resumeid || primaryResumeId;

    const emailPayload = {
      ...step3EmailData,
      candidateTotalExperience:
        step3EmailData?.candidateTotalExperience || step3EmailData?.total_experience,
      custom_body: step3EmailData?.customMode ? 1 : 0,
      signature_html: step3EmailData?.selectedSignatureHtml || "",
      provider,
      accessToken: provider === "gmail" || provider === "zoho" ? token : null,
      token: provider === "outlook" || provider === "zoho" ? token : null,
      accountsServer,
    };

    if (step3EmailData?.attachmentFile) {
      const file = step3EmailData.attachmentFile;
      emailPayload.customFile = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result.split(",")[1];
          resolve({
            name: file.name,
            contentBytes: base64,
            contentType: file.type || "application/octet-stream",
          });
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    }

    const res = await dispatch(
      sendCandidateProfileToVendors(
        emailPayload,
        currentUser?.id,
        candidateDetails?.candidate_id,
        selectedJobIds,
        selectedResumeId
      )
    );

    sessionStorage.removeItem("jobs_bulk_selected_ids");
    sessionStorage.removeItem("zoho_redirect_jobs");
    skipRestoreOnCloseRef.current = true;
    onClose({ success: true, summary: res?.data });
  };

  const handleSubmit = async (provider) => {
    handleMenuClose();

    try {
      if (!step3EmailData) {
        console.error("Please prepare the email content first");
        return;
      }

      setIsSubmitting(true);

      if (provider === "outlook") {
        const token = outlookToken || (await acquireOutlookToken());
        if (token) await handleSendEmail("outlook", token);
      } else if (provider === "gmail") {
        if (!gmailAccessToken) {
          gmailLogin();
          setIsSubmitting(false);
          return;
        }
        await handleSendEmail("gmail", gmailAccessToken);
      } else if (provider === "zoho") {
        if (!zohoEmail && !sessionStorage.getItem("zoho_email")) {
          handleZohoLogin();
          setIsSubmitting(false);
          return;
        }
        const currentToken = sessionStorage.getItem("zoho_token") || zohoAccessToken;
        const currentServer = sessionStorage.getItem("zoho_accounts_server") || zohoAccountsServer;
        await handleSendEmail("zoho", currentToken, currentServer);
      }
    } catch (error) {
      console.error(`${provider} send error:`, error);
      if (provider === "zoho") {
        setZohoEmail(null);
        setZohoAccessToken(null);
        setZohoAccountsServer(null);
        sessionStorage.removeItem("zoho_email");
        sessionStorage.removeItem("zoho_token");
        sessionStorage.removeItem("zoho_accounts_server");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (gmailAccessToken && step3EmailData && open) {
      handleSendEmail("gmail", gmailAccessToken);
    }
  }, [gmailAccessToken]);

  return (
    <Modal
      open={open}
      onClose={() => onClose({ success: false })}
      closeAfterTransition
      TransitionComponent={SlideTransition}
      BackdropProps={{
        timeout: 500,
        onClick: () => onClose({ success: false }),
        sx: { backgroundColor: "rgba(0, 0, 0, 0.5)" },
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Paper
        elevation={4}
        sx={{
          position: "relative",
          margin: "auto",
          width: "55%",
          maxHeight: "85vh",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 2,
          borderRadius: 2,
          overflowY: "auto",
        }}
      >
        <Toolbar sx={{ minHeight: "48px !important", px: 0 }}>
          <Typography paragraph sx={{ m: 0, fontWeight: 600 }}>
            Send Profile to Vendor ({selectedJobIds.length} job
            {selectedJobIds.length !== 1 ? "s" : ""})
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Email Review" arrow placement="top">
            <IconButton
              sx={{
                backgroundImage: sendProfileToVendorEmailPreview
                  ? "linear-gradient(360deg, #406882 0%, #518aa7 50%, #6d9ec6 100%)"
                  : null,
                height: "30px",
                width: "30px",
                mr: "5px",
                border: !sendProfileToVendorEmailPreview ? "1px solid #ddd" : "none",
              }}
              onClick={() => setSendProfileToVendorEmailPreview(true)}
            >
              <EmailRounded
                sx={{
                  color: sendProfileToVendorEmailPreview ? "#fff" : null,
                  height: "18px",
                  width: "18px",
                }}
              />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit Email" arrow placement="top">
            <IconButton
              sx={{
                backgroundImage: !sendProfileToVendorEmailPreview
                  ? "linear-gradient(360deg, #406882 0%, #518aa7 50%, #6d9ec6 100%)"
                  : null,
                height: "30px",
                width: "30px",
                border: sendProfileToVendorEmailPreview ? "1px solid #ddd" : "none",
              }}
              onClick={() => setSendProfileToVendorEmailPreview(false)}
            >
              <EditRounded
                sx={{
                  color: !sendProfileToVendorEmailPreview ? "#fff" : null,
                  height: "18px",
                  width: "18px",
                }}
              />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            disableElevation
            size="small"
            sx={{
              textTransform: "capitalize",
              ml: "5px",
              backgroundImage:
                "linear-gradient(360deg, #406882 0%, #518aa7 50%, #6d9ec6 100%)",
            }}
            onClick={handleMenuClick}
            endIcon={<KeyboardArrowDown />}
            disabled={isSubmitting || !selectedJobIds.length}
          >
            {isSubmitting ? <CircularProgress size={20} color="inherit" /> : "Send Email"}
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem onClick={() => handleSubmit("outlook")}>
              <ListItemIcon>
                <PiMicrosoftOutlookLogoFill fontSize="large" />
              </ListItemIcon>
              <ListItemText>Microsoft Outlook</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleSubmit("gmail")}>
              <ListItemIcon>
                <BiLogoGmail fontSize="large" />
              </ListItemIcon>
              <ListItemText>Send via Gmail</ListItemText>
            </MenuItem>
            {/* <MenuItem onClick={() => handleSubmit("zoho")}>
              <ListItemIcon>
                <SiZoho size={24} color="#e25c5c" />
              </ListItemIcon>
              <ListItemText>
                {zohoEmail || sessionStorage.getItem("zoho_email")
                  ? "Send via Zoho"
                  : "Authenticate Zoho"}
              </ListItemText>
            </MenuItem> */}
          </Menu>
            <Button
              size="small"
              sx={{ ml: 1, textTransform: "capitalize" }}
              onClick={() => onClose({ success: false })}
            >
              Cancel
            </Button>
        </Toolbar>
        <Divider />
        {sendProfileToVendorEmailPreview ? (
          <SendCandidateToVendorEmail
            step3EmailData={step3EmailData}
            selectedCandidateData={candidateDetails}
            hideToEmail
          />
        ) : (
          <Step3
            sharedData={sharedData}
            step3EmailDataHandler={step3EmailDataHandler}
            hideToEmail
          />
        )}
      </Paper>
    </Modal>
  );
}
